import React, { useContext } from 'react'
import * as Styled from './NursingCalculator.styles'
import {
  Button,
  DataTable,
  DataTd,
  DataTh,
  Select,
  Tooltip,
} from 'components/atoms'
import { Accordion } from 'components/molecules/index'
import {
  arrayOf,
  bool,
  number,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { TextContext } from 'hocs'
import { formatPrice } from 'settings/app/helpers'
import {
  DAY_AND_NIGHT_CARE,
  LONG_TERM_CARE,
  SHORT_TERM_CARE,
} from '../constants'

const NursingCalculator = ({
  type,
  careData,
  selectedCareFocus,
  activeVisitingDays,
  selectedVisitingDays,
  selectedFullStationaryTimeSpan,
  handleCareFocusSelect,
  handleFullStationaryTimeSpanSelect,
  handleVisitingDaysInput,
  handleCalculateButtonClick,
}) => {
  const textContext = useContext(TextContext)

  const currentCareLevelData =
    careData.careFocus.find(
      (careFocusData) => careFocusData.focusId === selectedCareFocus
    )?.careLevels || []

  /**
   * condenses the careLevels higher than 1 into one single entry
   */
  const getCondensedCareLevelData = () => {
    const leveOneIdx = currentCareLevelData.findIndex(
      (el) => el.careLevel === 1
    )
    const tempCareLevelData = currentCareLevelData.slice(leveOneIdx + 1)
    let min = 5,
      max = 2

    tempCareLevelData.forEach((tempEl) => {
      if (tempEl.careLevel > max) max = tempEl.careLevel
      if (tempEl.careLevel < min) min = tempEl.careLevel
    })

    if (leveOneIdx > -1) {
      return [
        currentCareLevelData[leveOneIdx],
        {
          ...tempCareLevelData[0],
          careLevel: max !== min ? `${ min }-${ max }` : min,
        },
      ]
    }
    return [
      {
        ...tempCareLevelData[0],
        careLevel: max !== min ? `${ min }-${ max }` : min,
      },
    ]
  }

  return (
    <Styled.NursingCalculator data-cy={ 'nursing-calculator' }>
      {/* header */}
      <div className='headline-container'>
        {type === DAY_AND_NIGHT_CARE && (
          <p
            data-cy={ 'cost-info' }
            dangerouslySetInnerHTML={{ __html: careData.costInformation }}
          />
        )}
        <div className='headline'>
          <span data-cy={ 'nursing-calculator-headline' }>
            {textContext[`nursingCalculator.${ type }.title`]}
          </span>
        </div>
        {type === SHORT_TERM_CARE && (
          <p
            data-cy={ 'cost-info' }
            dangerouslySetInnerHTML={{ __html: careData.costInformation }}
          />
        )}
      </div>
      {careData.careFocus.length > 0 && (
        <div className={ 'table-cell-style' }>
          <div className={ 'flex-row' }>
            <label>{textContext['nursingCalculator.select.label']}</label>
            <Select
              id={ 'calculator-select' }
              className={ 'thead-select' }
              data-cy={ 'calculator-select' }
              value={ selectedCareFocus }
              options={ careData.careFocus.map((careFocusData) => ({
                value: careFocusData.focusId,
                option: careFocusData.focus,
              })) }
              handleSelect={ (value) => handleCareFocusSelect(value) }
              excludeCurrentValue={ true }
              disabled={ careData.careFocus.length <= 1 }
            />
          </div>
          {type === LONG_TERM_CARE && (
            <div className={ 'flex-row' }>
              <label>
                {textContext['nursingCalculator.durationSelect.label']}
              </label>
              <div className={ 'flex-row info-icon-select' }>
                <Select
                  id={ 'calculator-select-timespan' }
                  className={ 'thead-select' }
                  data-cy={ 'calculator-select-timespan' }
                  value={ selectedFullStationaryTimeSpan }
                  options={ careData.fullStationaryTimeSpan.map(
                    (timeSpanData) => ({
                      value: timeSpanData.id,
                      option: timeSpanData.label,
                    })
                  ) }
                  handleSelect={ (value) =>
                    handleFullStationaryTimeSpanSelect(value)
                  }
                  excludeCurrentValue={ true }
                  disabled={ careData.fullStationaryTimeSpan.length <= 1 }
                />
                {!!careData.durationInformation && (
                  <Tooltip
                    infoIconId={ 'calculator-select-timespan-icon' }
                    tooltipId={ 'calculator-select-timespan-tip' }>
                    {careData.durationInformation}
                  </Tooltip>
                )}
              </div>
            </div>
          )}
          {(type === SHORT_TERM_CARE || type === DAY_AND_NIGHT_CARE) && (
            <>
              <div className={ 'flex-row' }>
                <label>{textContext['nursingCalculator.input.label']}</label>
                <input
                  type={ 'number' }
                  name={ 'visiting-days-input' }
                  className={ 'thead-input' }
                  data-cy={ 'calculator-input' }
                  value={ selectedVisitingDays }
                  placeholder={ 'z.B. 7' }
                  min={ 0 }
                  max={ 31 }
                  onChange={ (e) => handleVisitingDaysInput(e.target.value) }
                />
              </div>
              <div className={ 'flex-row' }>
                <Button
                  name={ 'calculate-button' }
                  data-cy={ 'calculator-button' }
                  text={ 'Preise berechnen' }
                  onClick={ () => handleCalculateButtonClick() }
                />
              </div>
            </>
          )}
        </div>
      )}
      <DataTable className={ 'nursing-calculator-table' }>
        <thead>
          {type === LONG_TERM_CARE && (
            <>
              <tr>
                <DataTh rowSpan={ '2' } className={ 'center-text' }>
                  {textContext['nursingCalculator.shortTermCare.careLevel']}
                </DataTh>
                <DataTh
                  colSpan={ '2' }
                  multiRow={ 'start' }
                  className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.careLevelPrice'
                    ]
                  }
                </DataTh>
                <DataTh rowSpan={ '2' } className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.accommodationCateringExpenses'
                    ]
                  }
                </DataTh>
                <DataTh rowSpan={ '2' } className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.investmentCost'
                    ]
                  }
                </DataTh>
                <DataTh
                  rowSpan={ '2' }
                  className={ 'center-text' }
                  style={{ fontWeight: 'bold' }}>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.calculatedShareTotal'
                    ]
                  }
                </DataTh>
              </tr>
              <tr>
                <DataTh
                  multiRow={ 'end' }
                  multiCol={ 'start' }
                  className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.careLevelPrice.eeE'
                    ]
                  }
                </DataTh>
                <DataTh
                  multiRow={ 'end' }
                  multiCol={ 'end' }
                  className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.careLevelPrice.carePlace'
                    ]
                  }
                </DataTh>
              </tr>
            </>
          )}

          {(type === SHORT_TERM_CARE || type === DAY_AND_NIGHT_CARE) && (
            <>
              <tr>
                <DataTh rowSpan={ '2' } className={ 'center-text' }>
                  {textContext['nursingCalculator.shortTermCare.careLevel']}
                </DataTh>
                <DataTh rowSpan={ '2' } className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.careLevelPrice.carePlacePerDay'
                    ]
                  }
                </DataTh>
                <DataTh rowSpan={ '2' } className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.investmentCostVisitingDays'
                    ]
                  }
                </DataTh>
                <DataTh
                  colSpan={ '2' }
                  multiRow={ 'start' }
                  style={{ fontWeight: 'bold' }}
                  className={ 'center-text' }>
                  {
                    textContext[
                      'nursingCalculator.shortTermCare.calculatedShare'
                    ]
                  }
                </DataTh>
              </tr>
              <tr>
                <DataTh
                  multiRow={ 'end' }
                  multiCol={ 'start' }
                  className={ 'center-text' }
                  data-cy='visiting-days'>
                  {`${
                    activeVisitingDays && activeVisitingDays > 0
                      ? activeVisitingDays
                      : 'X'
                  } ${ activeVisitingDays === 1 ? 'Tag' : 'Tage' }`}
                </DataTh>
                <DataTh
                  multiRow={ 'end' }
                  multiCol={ 'end' }
                  className={ 'center-text' }>
                  30 Tage
                </DataTh>
              </tr>
            </>
          )}
        </thead>
        <tbody>
          {type === LONG_TERM_CARE &&
            getCondensedCareLevelData().length > 0 &&
            getCondensedCareLevelData().map((price, index) => (
              <tr
                key={ index }
                data-cy={ `calculator-datarow-carelevel-${ price.careLevel }` }>
                <DataTd className={ 'center-text' } style={{ minWidth: 110 }}>
                  {price.careLevel}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 100 }}>
                  {formatPrice({
                    price: price.careLevel !== 1 ? price.ownShare : '',
                  })}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 100 }}>
                  {formatPrice({
                    price: price.careLevel === 1 ? price.ownShare : '',
                  })}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 180 }}>
                  {formatPrice({ price: price.housingAndCare })}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 120 }}>
                  {formatPrice({ price: price.invest })}
                </DataTd>
                <DataTd
                  className={ 'center-text' }
                  style={{
                    minWidth: 120,
                    fontWeight: price.priceMonth >= 0 ? 'bold' : 'lighter',
                  }}>
                  {price.priceMonth >= 0 ? (
                    formatPrice({ price: price.priceMonth })
                  ) : (
                    <Tooltip
                      infoIconId={ 'negativePriceMonth-icon-id' }
                      tooltipId={ 'negativePriceMonth-tooltip-id' }>
                      {textContext['nursingCalculator.negativePrice.info']}
                    </Tooltip>
                  )}
                </DataTd>
              </tr>
            ))}
          {(type === SHORT_TERM_CARE || type === DAY_AND_NIGHT_CARE) &&
            currentCareLevelData.length > 0 &&
            currentCareLevelData.map((price, index) => (
              <tr
                key={ index }
                data-cy={ `calculator-datarow-carelevel-${ price.careLevel }` }>
                <DataTd className={ 'center-text' } style={{ minWidth: 110 }}>
                  {price.careLevel}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 130 }}>
                  {formatPrice({ price: price.ownShare })}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 220 }}>
                  {formatPrice({ price: price.invest })}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 130 }}>
                  {price.priceDays >= 0 ? (
                    formatPrice({ price: price.priceDays })
                  ) : (
                    <Tooltip
                      infoIconId={ 'negativePriceDays-icon-id' }
                      tooltipId={ 'negativePriceDays-tip-id' }>
                      {textContext['nursingCalculator.negativePrice.info']}
                    </Tooltip>
                  )}
                </DataTd>
                <DataTd className={ 'center-text' } style={{ minWidth: 135 }}>
                  {price.priceMonth >= 0 ? (
                    formatPrice({ price: price.priceMonth })
                  ) : (
                    <Tooltip
                      infoIconId={ 'negativePriceMonth-icon-id' }
                      tooltipId={ 'negativePriceDays-tip-id' }>
                      {textContext['nursingCalculator.negativePrice.info']}
                    </Tooltip>
                  )}
                </DataTd>
              </tr>
            ))}
        </tbody>
      </DataTable>
      <div className='explanation-container' style={{ margin: '20px 0 30px' }}>
        {(type === SHORT_TERM_CARE ||
          type === LONG_TERM_CARE ||
          type === DAY_AND_NIGHT_CARE) &&
          careData.additionalInformation.length > 0 && (
            <Accordion
              type={ 'UNDERLINED' }
              label={ 'Erläuterungen und Zusatzinformationen' }
              data-cy={ 'additional-info-accordion' }>
              <div>
                <p
                  data-cy={ 'cost-info' }
                  className={ 'cost-info' }
                  dangerouslySetInnerHTML={{
                    __html: careData.additionalInformation,
                  }}
                />
              </div>
            </Accordion>
          )}
        {type === DAY_AND_NIGHT_CARE &&
          careData.transportInformation.length > 0 && (
            <div>
              <span
                className={ 'info-headline' }
                data-cy={ 'nursing-calculator-headline' }>
                Fahrtkosten
              </span>
              <p
                data-cy={ 'transport-info' }
                dangerouslySetInnerHTML={{
                  __html: careData.transportInformation,
                }}
              />
            </div>
          )}
      </div>
    </Styled.NursingCalculator>
  )
}

NursingCalculator.propTypes = {
  type: oneOf([DAY_AND_NIGHT_CARE, SHORT_TERM_CARE, LONG_TERM_CARE]).isRequired,
  careData: shape({
    careFocus: arrayOf(
      shape({
        abCode: string.isRequired,
        focus: string.isRequired,
        focusId: string.isRequired,
        careLevels: arrayOf(
          shape({
            careLevel: number,
            ownShare: number,
            invest: number,
            priceDays: number,
            priceMonth: number,
            housingAndCare: number,
          })
        ),
      })
    ).isRequired,
    fullStationaryTimeSpan: arrayOf(
      shape({
        id: string.isRequired,
        label: string.isRequired,
      })
    ),
    additionalInformation: string.isRequired,
    costInformation: string,
    transportInformation: string,
  }).isRequired,
  defaultVisitingDays: oneOfType([number, bool]),
}

export default NursingCalculator
